import { generateRandomString } from '../utils/unique-string';

class Tabs {
  constructor(options) {
    this.tabList = options.tablist || null;
    this.tabButtons =
      options.tabButtons || this.tabList.querySelectorAll('[role=tab]') || [];
    this.tabPanels =
      options.tabPanels ||
      this.tabList.querySelectorAll('[role=tabpanel]') ||
      [];
    this.onBeforeSelect =
      typeof options.onBeforeSelect === 'function'
        ? options.onBeforeSelect
        : null;
    this.onAfterSelect =
      typeof options.onAfterSelect === 'function'
        ? options.onAfterSelect
        : null;
    this.onDestroy =
      typeof options.onDestroy === 'function' ? options.onDestroy : null;
    this.initialTabIndex =
      options.initialTabIndex > 0 ? options.initialTabIndex : 0;
    this.currentTabIndex = this.initialTabIndex || 0;
    this.openClassName = options.openClassName || 'is-open';
    this.uniqueStr = generateRandomString();
  }

  init() {
    if (
      !this.tabList ||
      !this.tabButtons.length > 0 ||
      !this.tabPanels.length > 0
    )
      return;
    this.setInitialProperties();
    this.bindEvents();
    this.selectTab(this.initialTabIndex);
  }

  destroy() {
    this.onDestroy &&
      this.onDestroy({
        currentTabIndex: this.currentTabIndex,
        tabList: this.tabList,
        tabButtons: this.tabButtons,
        tabPanels: this.tabPanels,
      });
    this.unsetInitialProperties();
    this.unbindEvents();
    this.resetAllTabs();
  }

  setInitialProperties() {
    if (this.tabButtons.length !== this.tabPanels.length)
      console.warn(
        `There is an unequal amount of tabs (${this.tabButtons.length}) to panels (${this.tabPanels.length}).`
      );

    this.tabList.setAttribute('role', 'tablist');

    this.tabButtons.forEach((button, index) => {
      button.setAttribute('role', 'tab');
      button.setAttribute('id', `tab-${index}-${this.uniqueStr}`);
      button.setAttribute(
        'aria-controls',
        `tab-panel-${index}-${this.uniqueStr}`
      );
      button.setAttribute('aria-selected', false);
      button.setAttribute('tabindex', -1);
      button.setAttribute('data-tab-index', index);
    });
    this.tabPanels.forEach((panel, index) => {
      panel.setAttribute('role', 'tabpanel');
      panel.setAttribute('id', `tab-panel-${index}-${this.uniqueStr}`);
      panel.setAttribute(
        'aria-labelledby',
        `tab-panel-${index}-${this.uniqueStr}`
      );
      panel.setAttribute('tabindex', -1);
      panel.setAttribute('data-tab-index', index);
      panel.style.display = 'none';
    });
  }

  unsetInitialProperties() {
    if (this.tabList.getAttribute('role') === 'tablist')
      this.tabList.removeAttribute('role');

    this.tabButtons.forEach((button, index) => {
      if (button.getAttribute('role') === 'tab') button.removeAttribute('role');
      button.setAttribute(
        'id',
        button.getAttribute('id')?.replace(`tab-${index}-${this.uniqueStr}`, '')
      );
      button.setAttribute(
        'aria-controls',
        button
          .getAttribute('aria-controls')
          ?.replace(`tab-panel-${index}-${this.uniqueStr}`, '')
      );
      button.removeAttribute('aria-selected');
      if (button.getAttribute('tabindex') === '-1')
        button.removeAttribute('tabindex');
      button.removeAttribute('data-tab-index');
    });
    this.tabPanels.forEach((panel, index) => {
      if (panel.getAttribute('role') === 'tabpanel')
        panel.removeAttribute('role');
      panel.setAttribute(
        'id',
        panel
          .getAttribute('id')
          ?.replace(`tab-panel-${index}-${this.uniqueStr}`, '')
      );
      panel.setAttribute(
        'aria-labelledby',
        panel
          .getAttribute('aria-labelledby')
          ?.replace(`tab-panel-${index}-${this.uniqueStr}`, '')
      );
      if (panel.getAttribute('tabindex') === '-1')
        panel.removeAttribute('tabindex');
      panel.removeAttribute('data-tab-index');
      if (panel.style.display === 'none') panel.style.display = '';
    });
  }

  bindEvents() {
    this.tabButtons.forEach(button => {
      button.addEventListener('keyup', this.keyupHandler);
      button.addEventListener('click', this.clickHandler);
    });
  }

  unbindEvents() {
    this.tabButtons.forEach(button => {
      button.removeEventListener('keyup', this.keyupHandler);
      button.removeEventListener('click', this.clickHandler);
    });
  }

  selectTab(tabIndex) {
    this.onBeforeSelect &&
      this.onBeforeSelect({
        currentTabIndex: this.currentTabIndex,
        tabList: this.tabList,
        tabButtons: this.tabButtons,
        tabPanels: this.tabPanels,
      });

    this.tabButtons.forEach((button, i) => {
      if (tabIndex !== i) {
        button.setAttribute('aria-selected', false);
        button.setAttribute('tabindex', -1);
        this.tabPanels[i].classList.remove(this.openClassName);
        this.tabPanels[i].style.display = 'none';
      }

      if (tabIndex === i) {
        button.setAttribute('aria-selected', true);
        button.setAttribute('tabindex', 0);
        this.tabPanels[i].style.display = '';
        this.tabPanels[i].classList.add(this.openClassName);
        this.currentTabIndex = i;
      }
    });

    this.onAfterSelect &&
      this.onAfterSelect({
        currentTabIndex: this.currentTabIndex,
        tabList: this.tabList,
        tabButtons: this.tabButtons,
        tabPanels: this.tabPanels,
      });
  }

  resetAllTabs() {
    this.tabButtons.forEach((button, i) => {
      button.removeAttribute('aria-selected', false);
      if (this.tabPanels[i].style.display === 'none')
        this.tabPanels[i].style.display = '';
    });
  }

  focusTab(tabIndex) {
    this.tabButtons[tabIndex].focus();
    this.selectTab(tabIndex);
  }

  focusPrevTab() {
    if (this.currentTabIndex === 0) {
      this.focusTab(this.tabButtons.length - 1);
      return;
    }

    this.focusTab(this.currentTabIndex - 1);
  }

  focusNextTab() {
    if (this.currentTabIndex === this.tabButtons.length - 1) {
      this.focusTab(0);
      return;
    }

    this.focusTab(this.currentTabIndex + 1);
  }

  keyupHandler = event => {
    if (!event || !event.key) return;

    event.stopPropagation();
    event.preventDefault();

    if (event.key === 'ArrowLeft') this.focusPrevTab();
    if (event.key === 'ArrowRight') this.focusNextTab();
  };

  clickHandler = event => {
    if (!event || !event.currentTarget) return;

    this.selectTab(parseInt(event.currentTarget.dataset.tabIndex, 10));
  };
}

export { Tabs };
